import { Fragment } from 'react';
import { Menu, Portal } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { MdChevronRight, MdMoreVert } from 'react-icons/md';
import VButton from './VButton.jsx';
import VStackColumn from './VStackColumn.jsx';
import VStackRow from './VStackRow.jsx';
import VText from './VText.jsx';

const getItemStyles = ({ disabled, color }) => {
  if (disabled) {
    return { cursor: 'not-allowed' };
  }

  if (color === 'destructive') {
    return { cursor: 'pointer', color: 'fg.error', _hover: { bg: 'bg.error' } };
  }

  return { cursor: 'pointer' };
};

const renderItems = (items) =>
  items.map((item, index) => {
    if (item.children) {
      return (
        <Menu.Root key={item.value}>
          <Menu.TriggerItem
            value={item.value}
            disabled={item.disabled}
            onClick={(event) => {
              event.stopPropagation();
              if (item.onClick) {
                item.onClick();
              }
            }}
            css={
              item.disabled
                ? { cursor: 'not-allowed', _hover: { bg: 'transparent' } }
                : { cursor: 'pointer' }
            }
          >
            <VStackColumn flex={1} gap={0} align="flex-start">
              <VStackRow gap={2} align="center">
                {item.iconLeft}
                {item.label}
              </VStackRow>
              {item.description && (
                <VText fontSize="0.625rem" color={item.disabled ? 'gray' : 'inherit'}>
                  {item.description}
                </VText>
              )}
            </VStackColumn>
            <MdChevronRight
              style={{ marginLeft: 'auto', alignSelf: 'flex-start', marginTop: '4px' }}
            />
          </Menu.TriggerItem>
          <Portal>
            <Menu.Positioner>
              <Menu.Content
                css={{
                  _open: { animationDuration: '0s' },
                  _closed: { animationDuration: '0s' },
                }}
              >
                {renderItems(item.children)}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
          {item.separator && <Menu.Separator key={`sep-${index}`} />}
        </Menu.Root>
      );
    }

    return (
      <Fragment key={item.value}>
        <Menu.Item
          value={item.value}
          onClick={(event) => {
            event.stopPropagation();
            item.onClick?.(event);
          }}
          disabled={item.disabled}
          css={getItemStyles(item)}
        >
          {item.iconLeft}
          <VStackColumn flex={1} gap={0} align="flex-start">
            {item.label}
            {item.description && (
              <VText fontSize="0.625rem" color={item.disabled ? 'gray' : 'inherit'}>
                {item.description}
              </VText>
            )}
          </VStackColumn>
          {item.iconRight}
        </Menu.Item>
        {item.separator && <Menu.Separator />}
      </Fragment>
    );
  });

const triggerStyles = {
  default: { size: 'small', aspectRatio: '1' },
  small: { size: 'small', boxSize: '6', _icon: { boxSize: '6' } },
  normal: { size: 'normal', boxSize: '8', _icon: { boxSize: '8' } },
};

const VDotMenu = ({
  items,
  triggerLabel = undefined,
  disabled = false,
  size = 'default',
  ...props
}) => {
  return (
    <Menu.Root {...props}>
      <Menu.Trigger asChild>
        <VButton
          color="tertiary"
          px="0"
          {...triggerStyles[size]}
          aria-label={triggerLabel}
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
        >
          <MdMoreVert data-testid="dot-menu-icon" />
        </VButton>
      </Menu.Trigger>

      <Portal>
        <Menu.Positioner>
          <Menu.Content>{renderItems(items)}</Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

VDotMenu.propTypes = {
  size: PropTypes.oneOf(Object.keys(triggerStyles)),
};

export default VDotMenu;
